import React from 'react';
import {
    Box,
    Button, CircularProgress,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs, { Dayjs } from 'dayjs';
import { BackendPayload, FieldDef } from '../../types/forms';
import { ButtonRadioGroup } from '../radioButton/RadioButton';
import useFormulaRunner from '../../hooks/useFormulaRunner';

const buildZod = (defs: FieldDef[]) => {
    const shape: Record<string, z.ZodTypeAny> = {};
    defs.forEach((f) => {
        let schema: z.ZodTypeAny;
        if (f.type === 'number') {
            // let base = z.number({ error: 'Invalid number' });
            // if (typeof f.min === 'number') {
            //     base = base.min(f.min);
            // }
            // if (typeof f.max === 'number') {
            //     base = base.max(f.max);
            // }
            // schema = z.preprocess(
            //     (v) => (
            //         v === '' || v === null || v === undefined ? undefined : Number(v)
            //     ),
            //     base,
            // );
            schema = z.any();
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'email') {
            schema = z.string().email();
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'date') {
            schema = z
            .any()
            .refine(
                (v) => {
                    if (!f.required && (
                        v === null || v === undefined || v === ''
                    )) {
                        return true;
                    }
                    return dayjs(v as string | number | Date | Dayjs | null | undefined).isValid();
                },
                'Invalid date',
            );
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'radio' || f.type === 'select') {
            schema = z.string().or(z.number()).or(z.boolean());
            if (!f.required) {
                schema = schema.optional();
            }
        } else if (f.type === 'color') {
            schema = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Invalid color');
            if (!f.required) {
                schema = schema.optional();
            }
        } else {
            let base = z.string();
            if (f.pattern) {
                base = base.regex(new RegExp(f.pattern));
            }
            if (typeof f.min === 'number') {
                base = base.min(f.min);
            }
            if (typeof f.max === 'number') {
                base = base.max(f.max);
            }
            schema = base;
            if (!f.required) {
                schema = schema.optional();
            }
        }
        shape[f.id] = schema;
    });
    return z.object(shape);
};

type DynamicFormProps = {
    config: BackendPayload;
    onSubmit: (values: any) => Promise<void> | void;
    apiSubmitting?: boolean;
    saveButtonLabel?: string;
    hasCancelButton?: boolean;
    onCancel?: () => void;
    skin?: 'skin1' | 'skin2';
};

const computeActiveFields = (all: FieldDef[], values: Record<string, any>): FieldDef[] => {
    const activeGroups = new Set<string>([ 'root' ]);
    for (const f of all) {
        if ((
            f.type === 'select' || f.type === 'radio'
        ) && f.options && f.id in values) {
            const selected = values[f.id];
            const opt = f.options.find((o) => String(o.value) === String(selected));
            if (opt?.group) {
                activeGroups.add(String(opt.group));
            }
        }
    }
    return all.filter((f) => !f.group || f.group === 'root' || activeGroups.has(String(f.group)));
};

type InnerProps = {
    fields: FieldDef[],
    schema: z.ZodObject<any>,
    defaults: Record<string, any>,
    onSubmit: (vals: any) => void | Promise<void>,
    onValuesChange: (vals: Record<string, any>) => void,
    apiSubmitting: boolean,
    saveButtonLabel?: string,
    hasCancelButton?: boolean,
    onCancel?: () => void,
    skin: 'skin1' | 'skin2',
};

const DynamicFormInner: React.FC<InnerProps> = ({
    fields,
    schema,
    defaults,
    onSubmit,
    onValuesChange,
    apiSubmitting,
    saveButtonLabel = 'Submit',
    hasCancelButton = false,
    onCancel,
}) => {
    const {
        control,
        getValues,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: defaults,
        mode: 'onBlur',
    });
    const formulaRunner = useFormulaRunner();
    const watched = useWatch({ control });
    React.useEffect(() => {
        onValuesChange(watched || {});
    }, [ watched, onValuesChange ]);
    const submit: SubmitHandler<any> = async (vals) => {
        await onSubmit(vals);
    };

    const getFormulaResult = (formula: string): string => {
        const data = getValues();
        const result = formulaRunner(formula, data);
        console.log(result);
        return result as string;
    };

    return (
        <Box component="form" onSubmit={handleSubmit(submit)} noValidate>
            <Stack spacing={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {fields.map((f) => {
                    if (f.type === 'string') {
                        return (
                            <div key={f.id} style={{ display: 'flex', marginBottom: 8 }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        px: 2,
                                        py: 1.25,
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        borderRadius: 1,
                                        color: 'text.primary',
                                        fontSize: '1rem',
                                        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                        lineHeight: '1.4375em',
                                    }}
                                >
                                    {f.label}
                                </Box>
                            </div>
                        );
                    }
                    if (f.type === 'text' || f.type === 'email' || f.type === 'number' || f.type === 'password') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={f.label}
                                        type={f.type === 'number' ? 'number' : f.type}
                                        error={!!errors[f.id]}
                                        helperText={(
                                            errors[f.id]?.message as string
                                        ) || ''}
                                    />
                                )}
                            />
                        );
                    }
                    if (f.type === 'select') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label={f.label}
                                        error={!!errors[f.id]}
                                        helperText={(
                                            errors[f.id]?.message as string
                                        ) || ''}
                                    >
                                        {(
                                            f.options || []
                                        ).map((o) => (
                                            <MenuItem key={String(o.value)} value={o.value as any}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        );
                    }
                    if (f.type === 'radio') {
                        return (
                            <>
                                <Typography>{f.label}</Typography>
                                <Controller
                                    key={f.id}
                                    name={f.id}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioGroup {...field} row>
                                            {(
                                                f.options || []
                                            ).map((o) => (
                                                <FormControlLabel
                                                    key={String(o.value)}
                                                    value={o.value as any}
                                                    control={
                                                        <Radio/>
                                                    }
                                                    label={o.label}
                                                />
                                            ))}
                                        </RadioGroup>
                                    )}
                                />
                            </>
                        );
                    }
                    if (f.type === 'date') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        label={f.label}
                                        value={field.value ? dayjs(field.value as string | number | Date | Dayjs) : null}
                                        onChange={(v) => field.onChange(v ? (
                                            v as Dayjs
                                        ).toISOString() : null)}
                                        slotProps={{
                                            textField: {
                                                error: !!errors[f.id],
                                                helperText: (
                                                    errors[f.id]?.message as string
                                                ) || '',
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        );
                    }
                    if (f.type === 'color') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <Box>
                                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                                            {f.label}
                                        </Typography>
                                        <TextField
                                            type="color"
                                            value={field.value || '#000000'}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                p: 0,
                                                '& .MuiInputBase-root': { p: 0, height: '100%', borderRadius: 1 },
                                                '& fieldset': { display: 'none' },
                                                '& input[type="color"]': {
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    border: 'none',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'none',
                                                    borderRadius: 1,
                                                    WebkitAppearance: 'none',
                                                },
                                                '& input[type="color"]::-webkit-color-swatch-wrapper': {
                                                    padding: 0,
                                                    borderRadius: 1,
                                                },
                                                '& input[type="color"]::-webkit-color-swatch': {
                                                    border: '1px solid rgba(0,0,0,0.35)',
                                                    borderRadius: 4,
                                                },
                                            }}
                                        />
                                        {!!errors[f.id] && (
                                            <Typography variant="caption" color="error">
                                                {(
                                                    errors[f.id]?.message as string
                                                ) || ''}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            />
                        );
                    }
                    if (f.type === 'formula') {
                        return (
                            <div key={f.id} style={{ display: 'flex', marginBottom: 8 }}>
                                <div style={{ width: 200, fontSize: 14, fontWeight: 'bold' }}>
                                    {f.label}
                                </div>
                                <div>
                                    <Box
                                        sx={{
                                            width: 266,
                                            px: 2,
                                            py: 1.25,
                                            border: '1px solid rgba(0, 0, 0, 0.23)',
                                            borderRadius: 1,
                                            color: 'text.primary',
                                            fontSize: '1rem',
                                            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                            lineHeight: '1.4375em',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        {getFormulaResult(f.formula)}
                                    </Box>
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
                {hasCancelButton ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            startIcon={isSubmitting || apiSubmitting ? <CircularProgress size={16}/> : undefined}
                            variant="outlined"
                            disabled={isSubmitting || apiSubmitting}
                            onClick={onCancel}
                            sx={{ width: '49%' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            startIcon={isSubmitting || apiSubmitting ? <CircularProgress size={16}/> : undefined}
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || apiSubmitting}
                            sx={{ width: '49%', backgroundColor: '#013b71' }}
                        >
                            {saveButtonLabel}
                        </Button>
                    </div>
                ) : (
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || apiSubmitting}
                        startIcon={isSubmitting || apiSubmitting ? <CircularProgress size={16}/> : undefined}
                        style={{ backgroundColor: '#013b71' }}
                    >
                        {saveButtonLabel}
                    </Button>
                )}
            </Stack>
        </Box>
    );
};

const DynamicFormInner3: React.FC<InnerProps> = ({
    fields,
    schema,
    defaults,
    onSubmit,
    onValuesChange,
    apiSubmitting,
    saveButtonLabel = 'Submit',
    hasCancelButton = false,
    onCancel,
}) => {
    const {
        control,
        handleSubmit,
        getValues,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: defaults,
        mode: 'onBlur',
    });
    const formulaRunner = useFormulaRunner();
    const watched = useWatch({ control });
    React.useEffect(() => {
        onValuesChange(watched || {});
    }, [ watched, onValuesChange ]);

    const getFormulaResult = (formula: string): string => {
        const data = getValues();
        const mergedData = {};
        Object.keys(data).forEach(key => {
            // @ts-ignore
            mergedData[key] = { value: data[key], points: 1 };
        });
        const result = formulaRunner(formula, mergedData);
        console.log(result);
        return result as string;
    };

    const submit: SubmitHandler<any> = async (vals) => {
        await onSubmit(vals);
    };
    return (
        <div style={{ border: '1px solid #013b71' }}>
            <div
                style={{
                    background: '#013b71',
                    color: '#fff',
                    fontWeight: 700,
                    padding: 8,
                }}
            >
                Fill in the form
            </div>
            <Box
                component="form"
                onSubmit={handleSubmit(submit)}
                noValidate
                style={{
                    backgroundColor: '#f8fafc',
                    padding: 16,
                }}
            >
                <Stack spacing={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {fields.map((f) => {
                        if (f.type === 'string') {
                            return (
                                <div key={f.id} style={{ display: 'flex', marginBottom: 8 }}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            px: 2,
                                            py: 1.25,
                                            border: '1px solid rgba(0, 0, 0, 0.23)',
                                            borderRadius: 1,
                                            color: 'text.primary',
                                            fontSize: '1rem',
                                            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                            lineHeight: '1.4375em',
                                            '&:hover': {
                                                borderColor: 'text.primary',
                                            },
                                        }}
                                    >
                                        {f.label}
                                    </Box>
                                </div>
                            );
                        }
                        if (f.type === 'text' || f.type === 'email' || f.type === 'number' || f.type === 'password') {
                            return (
                                <Controller
                                    key={f.id}
                                    name={f.id}
                                    control={control}
                                    render={({ field }) => (
                                        <div style={{ display: 'flex', marginBottom: 8 }}>
                                            <div style={{
                                                width: 200,
                                                fontSize: 14,
                                                fontWeight: 'bold',
                                            }}>
                                                {f.label}
                                            </div>
                                            <div>
                                                <TextField
                                                    {...field}
                                                    label={f.label}
                                                    type={f.type}
                                                    error={!!errors[f.id]}
                                                    helperText={(
                                                        errors[f.id]?.message as string
                                                    ) || ''}
                                                    sx={{ width: 300 }}
                                                    size="small"
                                                />
                                            </div>
                                        </div>
                                    )}
                                />
                            );
                        }
                        if (f.type === 'select') {
                            return (
                                <Controller
                                    key={f.id}
                                    name={f.id}
                                    control={control}
                                    render={({ field }) => (
                                        <div style={{ display: 'flex', marginBottom: 8 }}>
                                            <div style={{
                                                width: 200,
                                                fontSize: 14,
                                                fontWeight: 'bold',
                                            }}>
                                                {f.label}
                                            </div>
                                            <div>
                                                <TextField
                                                    {...field}
                                                    select
                                                    label={f.label}
                                                    error={!!errors[f.id]}
                                                    helperText={(
                                                        errors[f.id]?.message as string
                                                    ) || ''}
                                                    sx={{
                                                        width: 300,
                                                        '& .MuiFormLabel-root:not(.Mui-focused)': {
                                                            transform: 'translate(14px, 8.5px) scale(1)',
                                                        },
                                                        '& .MuiSelect-select': {
                                                            py: 1, display: 'flex', alignItems: 'center',
                                                        },
                                                    }}
                                                >
                                                    {(
                                                        f.options || []
                                                    ).map((o) => (
                                                        <MenuItem key={String(o.value)} value={o.value as any}>
                                                            {o.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </div>
                                        </div>

                                    )}
                                />
                            );
                        }
                        if (f.type === 'radio') {
                            return (
                                <Controller
                                    key={f.id}
                                    name={f.id}
                                    control={control}
                                    render={({ field }) => (
                                        <div style={{ display: 'flex', marginBottom: 8 }}>
                                            <div style={{
                                                width: 200,
                                                fontSize: 14,
                                                fontWeight: 'bold',
                                            }}>
                                                {f.label}
                                            </div>
                                            <div>
                                                <ButtonRadioGroup
                                                    options={(
                                                        f.options || []
                                                    )}
                                                    value={field.value as any}
                                                    onChange={(v) => field.onChange(v)}
                                                    emptyDefaultValue
                                                />
                                            </div>
                                        </div>
                                    )}
                                />
                            );
                        }
                        if (f.type === 'date') {
                            return (
                                <Controller
                                    key={f.id}
                                    name={f.id}
                                    control={control}
                                    render={({ field }) => (
                                        <div style={{ display: 'flex', marginBottom: 8 }}>
                                            <div style={{
                                                width: 200,
                                                fontSize: 14,
                                                fontWeight: 'bold',
                                            }}>
                                                {f.label}
                                            </div>
                                            <div>
                                                <DatePicker
                                                    label={f.label}
                                                    value={field.value ? dayjs(field.value as string | number | Date | Dayjs) : null}
                                                    onChange={(v) => {
                                                        if (!v || !dayjs(v).isValid()) {
                                                            field.onChange(null);
                                                            return;
                                                        }
                                                        field.onChange(dayjs(v).toDate().toISOString());
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            error: !!errors[f.id],
                                                            helperText: (
                                                                errors[f.id]?.message as string
                                                            ) || '',
                                                            fullWidth: true,
                                                        },
                                                    }}
                                                    sx={{
                                                        width: 300,
                                                        '& .MuiFormLabel-root:not(.Mui-focused)': {
                                                            transform: 'translate(14px, 8.5px) scale(1)',
                                                        },
                                                        '& .MuiPickersSectionList-root': {
                                                            py: 1,
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                />
                            );
                        }
                        if (f.type === 'color') {
                            return (
                                <Controller
                                    key={f.id}
                                    name={f.id}
                                    control={control}
                                    render={({ field }) => (
                                        <div style={{ display: 'flex', marginBottom: 8 }}>
                                            <div style={{
                                                width: 200,
                                                fontSize: 14,
                                                fontWeight: 'bold',
                                            }}>
                                                {f.label}
                                            </div>
                                            <div>
                                                <Box>
                                                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                                                        {f.label}
                                                    </Typography>
                                                    <TextField
                                                        type="color"
                                                        value={field.value || '#000000'}
                                                        onChange={(e) => field.onChange(e.target.value)}
                                                        sx={{
                                                            width: 48,
                                                            height: 48,
                                                            p: 0,
                                                            '& .MuiInputBase-root': {
                                                                p: 0, height: '100%', borderRadius: 1,
                                                            },
                                                            '& fieldset': { display: 'none' },
                                                            '& input[type="color"]': {
                                                                cursor: 'pointer',
                                                                padding: 0,
                                                                border: 'none',
                                                                width: '100%',
                                                                height: '100%',
                                                                background: 'none',
                                                                borderRadius: 1,
                                                                WebkitAppearance: 'none',
                                                            },
                                                            '& input[type="color"]::-webkit-color-swatch-wrapper': {
                                                                padding: 0,
                                                                borderRadius: 1,
                                                            },
                                                            '& input[type="color"]::-webkit-color-swatch': {
                                                                border: '1px solid rgba(0,0,0,0.35)',
                                                                borderRadius: 4,
                                                            },
                                                        }}
                                                    />
                                                    {!!errors[f.id] && (
                                                        <Typography variant="caption" color="error">
                                                            {(
                                                                errors[f.id]?.message as string
                                                            ) || ''}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </div>
                                        </div>
                                    )}
                                />
                            );
                        }
                        if (f.type === 'formula') {
                            return (
                                <div key={f.id} style={{ display: 'flex', marginBottom: 8 }}>
                                    <div style={{ width: 200, fontSize: 14, fontWeight: 'bold' }}>
                                        {f.label}
                                    </div>
                                    <div>
                                        <Box
                                            sx={{
                                                width: 266,
                                                px: 2,
                                                py: 1.25,
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                borderRadius: 1,
                                                color: 'text.primary',
                                                fontSize: '1rem',
                                                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                                lineHeight: '1.4375em',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            {getFormulaResult(f.formula)}
                                        </Box>
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })}
                    {hasCancelButton ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button
                                startIcon={isSubmitting || apiSubmitting ?
                                    <CircularProgress size={16}/> : undefined}
                                variant="outlined"
                                disabled={isSubmitting || apiSubmitting}
                                onClick={onCancel}
                                sx={{ width: '49%' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                startIcon={isSubmitting || apiSubmitting ?
                                    <CircularProgress size={16}/> : undefined}
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting || apiSubmitting}
                                sx={{ width: '49%', backgroundColor: '#013b71' }}
                            >
                                {saveButtonLabel}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || apiSubmitting}
                            startIcon={isSubmitting || apiSubmitting ?
                                <CircularProgress size={16}/> : undefined}
                            style={{ backgroundColor: '#013b71' }}
                        >
                            {saveButtonLabel}
                        </Button>
                    )}
                </Stack>
            </Box>
        </div>
    );
};

const DynamicForm: React.FC<DynamicFormProps> = ({
    config,
    onSubmit,
    apiSubmitting = false,
    saveButtonLabel,
    hasCancelButton = false,
    onCancel,
    skin = 'skin2',
}) => {
    const defaults = React.useMemo(() => {
        const d: Record<string, any> = {};
        config.fields.forEach((f) => {
            if (f.defaultValue !== undefined) {
                d[f.id] = f.defaultValue;
            } else if (f.type === 'date') {
                d[f.id] = null;
            } else if (f.type === 'color') {
                d[f.id] = '#000000';
            } else {
                d[f.id] = '';
            }
        });
        return d;
    }, [ config.fields ]);

    const [ valuesState, setValuesState ] = React.useState<Record<string, any>>(defaults);
    const activeFields = React.useMemo(() => computeActiveFields(config.fields, valuesState), [ config.fields, valuesState ]);
    const schema = React.useMemo(() => buildZod(activeFields), [ activeFields ]);
    const activeKey = React.useMemo(() => activeFields.map((f) => f.id).join('|'), [ activeFields ]);

    React.useEffect(() => {
        const activeNames = new Set(activeFields.map((f) => f.id));
        let changed = false;
        const next = { ...valuesState };
        for (const f of config.fields) {
            if (!activeNames.has(f.id)) {
                const target = defaults[f.id];
                if (next[f.id] !== target) {
                    next[f.id] = target;
                    changed = true;
                }
            }
        }
        if (changed) {
            setValuesState(next);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ activeFields, config.fields, defaults ]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            {skin === 'skin1' ? (
                <div style={{ maxWidth: 500, padding: 16 }}>
                    <DynamicFormInner
                        key={activeKey}
                        fields={activeFields}
                        schema={schema}
                        defaults={valuesState}
                        onSubmit={onSubmit}
                        onValuesChange={setValuesState}
                        apiSubmitting={apiSubmitting}
                        saveButtonLabel={saveButtonLabel}
                        hasCancelButton={hasCancelButton}
                        onCancel={onCancel}
                        skin={skin}
                    />
                </div>
            ) : skin === 'skin2' ? (
                <div style={{ width: 600, minWidth: 600 }}>
                    <DynamicFormInner3
                        key={activeKey}
                        fields={activeFields}
                        schema={schema}
                        defaults={valuesState}
                        onSubmit={onSubmit}
                        onValuesChange={setValuesState}
                        apiSubmitting={apiSubmitting}
                        saveButtonLabel={saveButtonLabel}
                        hasCancelButton={hasCancelButton}
                        onCancel={onCancel}
                        skin={skin}
                    />
                </div>
            ) : (
                <div>Skin is coming soon</div>
            )}

        </LocalizationProvider>
    );
};

export default DynamicForm;
